// Supplier invoices fetched from the Tax Authority, exported as an Xtram PDF or Excel report.
import * as XLSX from 'xlsx-js-style';
import { buildReportWorkbook } from './ExcelReport';
import EnumsService from '../Services/enums.service';
import { XTRAM_LOGOS } from '../assests/branding/xtram/logos';
import { AGING_REPORT_COLORS as C } from './receivablesAgingExport';

const STATUS = { UNMATCHED: 0, MATCHED: 1, LINKED: 2 };

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const safeFileName = (value) => String(value || 'Client').replace(/[\\/:*?"<>|]/g, '_');

const formatDisplayDate = (value) => {
    if (!value) return '';
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('he-IL', { timeZone: 'UTC' });
};

const formatAmount = (value) => {
    const parsed = Number(value ?? 0);
    return Number.isNaN(parsed)
        ? '0.00'
        : parsed.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const grossAmount = (invoice) => {
    const including = Number(invoice?.paymentAmountIncludingVat ?? 0);
    if (including) return including;
    return Number(invoice?.paymentAmount ?? 0) + Number(invoice?.vatAmount ?? 0);
};

const statusLabel = (row, t) => {
    if (row.status === STATUS.LINKED) return t('SupplierInvoiceAlreadyLinked');
    if (row.status === STATUS.MATCHED) return t('SupplierInvoiceMatched');
    return t('SupplierInvoiceMissing');
};

const systemText = (row, supplierNames, t) => {
    if (row.expense) {
        const parts = [`${t('UploadNumber')} ${row.expense.uploadNumber ?? ''}`];
        if (row.expense.description) parts.push(row.expense.description);
        (row.expense.differences || []).forEach((difference) => {
            const field = t(`SupplierInvoiceField_${difference.field}`, { defaultValue: difference.field });
            parts.push(`${field}: ${difference.systemValue || '—'} → ${difference.taxAuthorityValue || '—'}`);
        });
        return parts.join('\n');
    }
    if (row.supplierCardId) return t('SupplierInvoiceWillBeCreated');
    const typed = supplierNames?.[String(row?.invoice?.vatNumber ?? '')]?.trim();
    return typed || '';
};

const headers = (t) => [
    t('Supplier'),
    t('SupplierInvoiceSupplierVatFilter'),
    t('Reference'),
    t('Date'),
    t('Amount'),
    t('VAT'),
    t('ConfirmationNumber'),
    t('Status'),
    t('SupplierInvoiceInSystem'),
];

const dataRows = (rows, supplierNames, t) =>
    rows.map((row) => {
        const invoice = row.invoice || {};
        return [
            row.supplierCardName || t('UnknownSupplier'),
            invoice.vatNumber || '',
            invoice.invoiceReferenceNumber || '',
            formatDisplayDate(invoice.invoiceDate),
            round2(grossAmount(invoice)),
            round2(invoice.vatAmount),
            invoice.confirmationNumber || '',
            statusLabel(row, t),
            systemText(row, supplierNames, t),
        ];
    });

const totalRow = (rows, t) => {
    const amount = rows.reduce((sum, row) => sum + grossAmount(row.invoice), 0);
    const vat = rows.reduce((sum, row) => sum + Number(row.invoice?.vatAmount ?? 0), 0);
    return [t('Total'), '', '', '', round2(amount), round2(vat), '', '', ''];
};

const titleLines = ({ clientName, clientCase, startDate, endDate, dateBasisLabel, supplierVatNumber, t }) => {
    const period = [formatDisplayDate(startDate), formatDisplayDate(endDate)].filter(Boolean).join(' – ');
    const context = [dateBasisLabel, supplierVatNumber?.trim()].filter(Boolean).join(' · ');
    return [
        `${clientCase || ''}    ${clientName || ''}`.trim(),
        t('SupplierInvoicesFromTaxAuthority'),
        [period, context].filter(Boolean).join(' · '),
    ].filter(Boolean);
};

const fileBase = (clientName, clientCase) => `${safeFileName(clientName)}-${clientCase || ''}_SupplierInvoices`;

export const downloadSupplierInvoicesExcel = ({
    rows,
    clientName,
    clientCase,
    startDate,
    endDate,
    dateBasisLabel,
    supplierVatNumber,
    supplierNames,
    t,
}) => {
    const header = headers(t);
    const body = dataRows(rows, supplierNames, t);
    const tableRows = [...body, totalRow(rows, t)];
    const titles = titleLines({ clientName, clientCase, startDate, endDate, dateBasisLabel, supplierVatNumber, t });
    const wb = buildReportWorkbook({
        sheetName: 'Supplier invoices',
        titleLines: titles,
        header,
        rows: tableRows,
        colWidths: [24, 16, 16, 14, 14, 14, 20, 18, 36],
        numericColIndexes: [4, 5],
        totalRowIndexes: [tableRows.length - 1],
    });

    const ws = wb.Sheets[wb.SheetNames[0]];
    const firstDataRow = titles.length + 1 + 1;
    const systemCol = header.length - 1;
    body.forEach((row, i) => {
        const lines = String(row[systemCol] || '').split('\n');
        const cell = ws[XLSX.utils.encode_cell({ r: firstDataRow + i, c: systemCol })];
        if (cell) cell.s = { ...cell.s, alignment: { ...cell.s?.alignment, wrapText: true, vertical: 'top' } };
        if (lines.length > 1) ws['!rows'][firstDataRow + i] = { hpt: Math.max(18, lines.length * 15 + 4) };
    });

    XLSX.writeFile(wb, `${fileBase(clientName, clientCase)}.xlsx`);
};

const assetToDataUrl = async (assetUrl) => {
    try {
        const res = await fetch(assetUrl);
        if (!res.ok) return '';
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    } catch {
        return '';
    }
};

const buildPdfHtml = ({
    rows,
    clientName,
    clientCase,
    startDate,
    endDate,
    dateBasisLabel,
    supplierVatNumber,
    supplierNames,
    logoDataUrl,
    t,
}) => {
    const header = headers(t);
    const body = dataRows(rows, supplierNames, t);
    const totals = totalRow(rows, t);
    const numericCols = new Set([4, 5]);
    const cell = (value, index, negative) => {
        if (numericCols.has(index)) {
            return `<td class="num${negative ? ' negative' : ''}">${escapeHtml(formatAmount(value))}</td>`;
        }
        const text = String(value ?? '').replace(/\n/g, '<br/>');
        return `<td class="${index === header.length - 1 ? 'sys' : 'txt'}">${text
            .split('<br/>')
            .map((part) => escapeHtml(part))
            .join('<br/>')}</td>`;
    };
    const bodyHtml = body
        .map((row, idx) => {
            const amountNegative = Number(row[4]) < 0;
            return `<tr class="${idx % 2 === 1 ? 'stripe' : 'plain'}">${row
                .map((value, index) => cell(value, index, index === 4 && amountNegative))
                .join('')}</tr>`;
        })
        .join('');
    const totalHtml = `<tr class="total">${totals.map((value, index) => cell(value, index, false)).join('')}</tr>`;
    const titles = titleLines({ clientName, clientCase, startDate, endDate, dateBasisLabel, supplierVatNumber, t });
    const now = new Date();
    const printedAt = `${now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} ${now.toLocaleDateString('he-IL')}`;

    return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(t('SupplierInvoicesFromTaxAuthority'))}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm 8mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, "Segoe UI", sans-serif; direction: rtl; color: ${C.text}; margin: 0; padding: 6px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 11px; }
    .top-bar img { height: 28px; }
    .center-block { text-align: center; flex: 1; }
    h1 { margin: 2px 0 4px; font-size: 18px; color: ${C.primary}; font-weight: 700; }
    .meta { font-size: 11px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid ${C.border}; padding: 5px 4px; text-align: center; vertical-align: top; }
    th { background: ${C.primary} !important; color: ${C.headerFont} !important; font-weight: 700; }
    td.txt, td.sys { text-align: right; }
    td.sys { line-height: 1.45; }
    td.num { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
    td.negative { color: #c0392b; }
    tr.plain td { background: ${C.plain} !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td { font-weight: 700; background: ${C.totalFill} !important; color: ${C.primaryDark} !important; }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${logoDataUrl ? `<img src="${logoDataUrl}" alt="Xtram" />` : ''}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(titles[0] || '')}</div>
      <h1>${escapeHtml(titles[1] || t('SupplierInvoicesFromTaxAuthority'))}</h1>
      <div class="meta">${escapeHtml(titles[2] || '')}</div>
    </div>
    <div>${escapeHtml(printedAt)}</div>
  </div>
  <table>
    <thead><tr>${header.map((label) => `<th>${escapeHtml(label)}</th>`).join('')}</tr></thead>
    <tbody>${bodyHtml}${totalHtml}</tbody>
  </table>
</body>
</html>`;
};

export const downloadSupplierInvoicesPdf = async ({
    rows,
    clientName,
    clientCase,
    startDate,
    endDate,
    dateBasisLabel,
    supplierVatNumber,
    supplierNames,
    token,
    t,
}) => {
    if (!token) throw new Error('Missing auth token');
    if (!clientCase) throw new Error('Missing case id');

    const logoDataUrl = await assetToDataUrl(XTRAM_LOGOS.wordmarkColorPng);
    const html = buildPdfHtml({
        rows,
        clientName,
        clientCase,
        startDate,
        endDate,
        dateBasisLabel,
        supplierVatNumber,
        supplierNames,
        logoDataUrl,
        t,
    });
    const res = await EnumsService.GeneratePdfFromHtml(html, clientCase, token);
    if (!res?.ok) {
        const errText = await res?.text?.().catch(() => '');
        throw new Error(errText || 'PDF generation failed');
    }

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const json = await res.json();
        const pdfBase64 = json.data?.PdfBase64 || json.data?.pdfBase64 || json.PdfBase64;
        if (!pdfBase64) throw new Error('No PDF base64 in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileBase(clientName, clientCase)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
